<template>
  <div class="strip-chart">
    <p class="text-center">
      <span v-if="isAnomaly">The average value for the month between {{whichAnomaly}}
        was {{avg_value}} {{weatherType.id}}. </span>
      The highest {{weatherType.type}} was {{maxValue.value}} {{weatherType.id}} in {{maxValue.year}}.
      The lowest {{weatherType.type}} was {{minValue.value}} {{weatherType.id}} in {{minValue.year}}</p>
    <legend-map :colors="strip_color" :type="stripType" :width="width"></legend-map>
    <svg height="110" :width="width_large - 100">
      <template v-for="d in currentData">
        <rect :x="xScale(d.year)" y="0"
              :height="height"
              :width="barsWidth"
              :fill="stripColors(d.value)"
              :transform="offset"
              :content="infoFormat(d)"
              v-tippy="{delay: 0, arrow: true, maxWidth: '210px'}"></rect>
      </template>
    </svg>
  </div>
</template>

<script>
  import legendMap from './legendMap.vue';
  import countyHelpers from "../mixins/countyHelpers";
  import * as d3 from 'd3';
  import floor from 'lodash.floor';
  import debounce from 'lodash.debounce';
  import round from 'lodash.round';

  const margins = { top: 20, right: 50, left: 25, bottom: 75 };

  export default {
    name: 'stripChart',

    components: {legendMap},

    mixins: [countyHelpers],

    props: {
      dataValues: {
        type: Array,
        default: []
      },
      dataAvgs: {
        type: Array,
        default: []
      },
      valueType: String,
      month: String,
      stripType: String,
      whichAnomaly: {
        type: String,
        default: '1981-2010'
      }
    },

    data() {
      return {
        anomaly: '1981-2010',
        avg_value: '',
        height: 80,
        offset: `translate(${margins.left},0)`,
        strip_color: [],
        avgs: [],
        width: window.innerWidth  - margins.left - margins.right,
        width_large: window.innerWidth  + margins.left + margins.right,
      }
    },

    watch: {
      dataValues() {
        this.strip_color = this.colorList(this.stripType);
      },

      dataAvgs(vals) {
        this.avgs = vals;
      },

      whichAnomaly(val) {
        this.anomaly = val;
      }
    },

    computed: {
      xScale() {
        let scale = d3.scaleTime()
          .range([0, this.width]);

        scale.domain(d3.extent(this.currentData, (d) => d.year));

        return scale;
      },

      barsWidth() {
        return floor(this.width / this.currentData.length, 3);
      },

      stripColors() {
        return d3.scaleQuantile()
          .domain(d3.extent(this.currentData, (d) => +d.value))
          .range(this.strip_color);
      },

      isAnomaly() {
        return this.valueType === 'weather-anomalies';
      },

      currentData() {
        let values = this.dataValues.filter((d) => {
          if ((d[this.month] !== '-99.90' && this.type !== 'precip') ||
            (d[this.month] !== '-9.99' && this.type === 'precip')) {
            return d;
          }
        });

        if (this.valueType === 'weather-anomalies') {
          return this.showAnomalies(values);
        }

        return values.map((d) => {
          return { year: d.year, value: +d[this.month] };
        });
      },

      maxValues() {
        return this.currentData[this.maxIndex(this.currentData, (d) => +d.value)];
      },

      minValues() {
        return this.currentData[this.minIndex(this.currentData, (d) => +d.value)];
      },

      maxValue() {
        if (this.maxValues === undefined) return {};
        return this.maxValues;
      },

      minValue() {
        if (this.minValues === undefined) return {};
        return this.minValues;
      },

      weatherType() {
        if (this.isAnomaly) {
          return /^t/.test(this.stripType) ? { type: 'temperature anomaly', id: 'degrees' } :
            { type: 'rainfall anomaly', id: 'inches' };
        }

        return /^t/.test(this.stripType) ? { type: 'temperature', id: 'degrees' } :
          { type: 'rainfall level', id: 'inches' };
      }
    },

    methods: {
      showAnomalies(values) {
        let avgs = this.avgs.find(d => d.month === this.month && d.type === this.anomaly);

        if (avgs !== undefined) {
          this.avg_value = avgs.avg;

          return values.map((d) => {
            return {
              year: d.year,
              value: round(+d[this.month] - +avgs.avg, 1)
            };
          });
        }
      },

      computeWidth() {
        this.width = window.innerWidth - margins.left - margins.right;
        this.width_large = window.innerWidth  + margins.left + margins.right;
      },

      infoFormat(values) {
        return `<p>${values.year}<br/>
                ${values.value} ${this.weatherType.id}</p>`;
      },

      // https://github.com/d3/d3-array/blob/master/src/minIndex.js
      // Should come in with d3, but doesn't seem to
      minIndex(values, valueof) {
        let min;
        let minIndex = -1;
        let index = -1;
        if (valueof === undefined) {
          for (const value of values) {
            ++index;
            if (value != null
              && (min > value || (min === undefined && value >= value))) {
              min = value, minIndex = index;
            }
          }
        } else {
          for (let value of values) {
            if ((value = valueof(value, ++index, values)) != null
              && (min > value || (min === undefined && value >= value))) {
              min = value, minIndex = index;
            }
          }
        }
        return minIndex;
      },

      // See https://github.com/d3/d3-array/blob/master/src/maxIndex.js
      // Should come in with d3, but doesn't seem to
      maxIndex(values, valueof) {
        let max;
        let maxIndex = -1;
        let index = -1;
        if (valueof === undefined) {
          for (const value of values) {
            ++index;
            if (value != null
              && (max < value || (max === undefined && value >= value))) {
              max = value, maxIndex = index;
            }
          }
        } else {
          for (let value of values) {
            if ((value = valueof(value, ++index, values)) != null
              && (max < value || (max === undefined && value >= value))) {
              max = value, maxIndex = index;
            }
          }
        }
        return maxIndex;
      }
    },

    mounted() {
      this.computeWidth();
    },

    beforeMount() {
      window.addEventListener('resize', debounce(this.computeWidth, 250));
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.computeWidth);
    }
  }
</script>

<style scoped>
  rect:hover {
    opacity: .8;
  }

  .tippy-popper {
    max-width: 180px;
  }

  .tippy-content p {
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/components/stripChart.vue